import React from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import IconButton from '@mui/material/IconButton';
import BlogsIcon from './BlogsIcon';
import { color, font } from '../styles/tokens';

const today = new Date().toLocaleDateString(undefined, {
  weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
}).replace(/,/g, '');

const Header = ({ user, onLogout, websiteCount }) => {
  const isNarrow = useMediaQuery('(max-width:600px)');

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 20, background: color.ink }}>
      <Box
        sx={{
          maxWidth: 1200, mx: 'auto', px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 },
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: { xs: 1, sm: 2 }, borderBottom: `1px solid ${color.rule}`, flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, minWidth: 0 }}>
          <Box
            className="wd-bracket"
            sx={{
              width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, borderRadius: '50%', background: '#fff',
              display: 'grid', placeItems: 'center', boxShadow: `0 0 8px ${color.cyanGlow}`,
              flex: 'none', overflow: 'hidden',
            }}
          >
            <BlogsIcon style={{ width: '100%', height: '100%', borderRadius: 0, boxShadow: 'none' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' }, color: color.bone, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
              Wire Desk
            </Typography>
            {!isNarrow && (
              <Typography sx={{
                fontFamily: font.mono, fontSize: '.6875rem', letterSpacing: '.1em', textTransform: 'uppercase', color: color.boneFaint,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                TGPS Global &middot; editorial control room
              </Typography>
            )}
          </Box>
        </Box>
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 }, flex: 'none' }}>
            <IconButton component={Link} to="/" title="Home" size={isNarrow ? 'small' : 'medium'} sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }}>
              <HomeIcon fontSize="small" />
            </IconButton>
            <IconButton component={Link} to="/dashboard" title="Dashboard" size={isNarrow ? 'small' : 'medium'} sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }}>
              <DashboardIcon fontSize="small" />
            </IconButton>
            {user.role === 'superadmin' && (
              <IconButton component={Link} to="/admin" title="Press room" size={isNarrow ? 'small' : 'medium'} sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }}>
                <AdminPanelSettingsIcon fontSize="small" />
              </IconButton>
            )}
            {isNarrow ? (
              <IconButton onClick={onLogout} title="Sign out" size="small" sx={{ ml: 0.5, color: color.boneDim, border: `1px solid ${color.rule}`, '&:hover': { color: color.cyan, borderColor: color.cyanDim } }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            ) : (
              <Button
                onClick={onLogout}
                startIcon={<LogoutIcon fontSize="small" />}
                variant="outlined"
                size="small"
                sx={{ ml: 1, whiteSpace: 'nowrap' }}
              >
                Sign out
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ background: color.panel, borderBottom: `1px solid ${color.cyanDim}`, boxShadow: `0 1px 14px -2px ${color.cyanGlow}` }}>
        <Box
          className="tabular"
          sx={{
            maxWidth: 1200, mx: 'auto', px: { xs: 1.5, sm: 2, md: 3 }, py: '.5em',
            display: 'flex', alignItems: 'center', gap: '.75em', flexWrap: 'wrap',
            fontFamily: font.mono, fontSize: { xs: '.6875rem', sm: '.75rem' }, color: color.boneDim,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 12, flex: 'none' }} aria-hidden="true">
            {[30, 55, 80, 100].map((h, i) => (
              <Box
                key={i}
                sx={{
                  width: 3, height: `${h}%`, background: color.teal,
                  boxShadow: `0 0 6px ${color.tealGlow}`,
                  animation: 'wd-bar 1.6s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </Box>
          <Box component="span" sx={{ color: color.bone, fontWeight: 500, whiteSpace: 'nowrap' }}>{today}</Box>
          {typeof websiteCount === 'number' && (
            <>
              <Box component="span" sx={{ color: color.boneFaint }}>&mdash;</Box>
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}><b style={{ color: color.bone }}>{websiteCount}</b> {websiteCount === 1 ? 'property' : 'properties'} on the wire</Box>
            </>
          )}
          {user && (
            <>
              <Box component="span" sx={{ color: color.boneFaint }}>&mdash;</Box>
              <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 140, sm: 'none' } }}>
                callsign: <b style={{ color: color.bone }}>{user.username}</b>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
